import { useCallback, useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import {
  MonitoringEnrolmentOptions,
  MonitoringEnrolmentResult,
  SubjectRegistration,
} from '@/types/monitoring';
import { toError } from '@/utils/errors';

/**
 * Whether a report can be enrolled into monitoring, and the actions to do it.
 *
 * `enabled` gates the fetch so the report page does not call this on every
 * view — the action is admin-only and rare, so the options load when the
 * analyst opens the modal rather than on mount.
 *
 * A report that cannot be enrolled is not an error: `options.available` is
 * false with a `blockedReason`, and the caller renders nothing or explains
 * why. `error` means one thing only — the options could not be loaded. A
 * failed *submission* is thrown to the caller instead, which keeps the
 * modal open and shows it against the selection that caused it.
 */
export function useMonitoringEnrolment(reportId: string | undefined, enabled: boolean) {
  const [options, setOptions] = useState<MonitoringEnrolmentOptions | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!reportId || !enabled) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    vnextApiClient
      .getMonitoringEnrolmentOptions(reportId)
      .then((result) => {
        if (!cancelled) {
          setOptions(result);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const e = err instanceof Error ? err : new Error('Failed to load monitoring options');
          Sentry.captureException(e);
          setError(e);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reportId, enabled]);

  /**
   * Re-read the options after an action that changes a subject's status.
   *
   * Quiet on purpose: no loading state, so the list stays on screen and
   * updates in place, and a failed re-read leaves the previous options up
   * rather than replacing them with an error about something that did work.
   */
  const refresh = useCallback(async () => {
    if (!reportId) {
      return;
    }
    try {
      setOptions(await vnextApiClient.getMonitoringEnrolmentOptions(reportId));
    } catch (err: unknown) {
      Sentry.captureException(toError(err, 'Failed to reload monitoring options'));
    }
  }, [reportId]);

  /**
   * Enrol the chosen subjects and components.
   *
   * Rejects rather than swallowing, so the caller can keep the modal open and
   * show what went wrong — a 400 here means the selection is not one this
   * report can offer, which the analyst needs to see rather than have silently
   * discarded.
   *
   * Resolves when the enrolment exists, even if Castellum refused the
   * registration: that is `result.registrationError`, and the options are
   * re-read so the enrolled subjects show as pending.
   */
  const enrol = useCallback(
    async (subjectIds: string[], componentSlugs: string[]): Promise<MonitoringEnrolmentResult> => {
      if (!reportId) {
        throw new Error('No report to enrol from');
      }
      setIsSubmitting(true);
      setError(null);
      try {
        const result = await vnextApiClient.createMonitoringEnrolment({
          reportId,
          subjectIds,
          componentSlugs,
        });
        await refresh();
        return result;
      } catch (err: unknown) {
        const e = err instanceof Error ? err : new Error('Failed to enrol into monitoring');
        Sentry.captureException(e);
        // Deliberately not stored on `error`. That field means "the options
        // could not be loaded"; a submit failure is handed back to the caller,
        // which owns showing it. Setting both renders the same message twice.
        throw e;
      } finally {
        setIsSubmitting(false);
      }
    },
    [reportId, refresh]
  );

  /**
   * Register an enrolled subject that is still pending, now.
   *
   * Resolves with Castellum's answer even when it refuses. Rejects only when
   * the request fails — a 409 when the subject is no longer pending. The
   * options are re-read either way, because both can mean the status on
   * screen is out of date.
   */
  const register = useCallback(
    async (monitoredSubjectId: string): Promise<SubjectRegistration> => {
      try {
        return await vnextApiClient.registerMonitoredSubject(monitoredSubjectId);
      } finally {
        await refresh();
      }
    },
    [refresh]
  );

  return { options, isLoading, isSubmitting, error, enrol, register };
}
